import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  FlatList,
  RefreshControl,
  SafeAreaView,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { driverService } from '../../services/driverService';

const isWeb = Platform.OS === 'web';

const EarningsScreen = () => {
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cashingOut, setCashingOut] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [walletRes, txnRes] = await Promise.all([
        driverService.getWallet().catch(() => null),
        driverService.getTransactions(1, 20).catch(() => null),
      ]);

      if (walletRes?.success) {
        setWallet(walletRes.data);
      }
      if (txnRes?.success) {
        setTransactions(txnRes.data.transactions);
        setHasMore(txnRes.data.pagination.page < txnRes.data.pagination.pages);
        setPage(1);
      }
    } catch (error) {
      console.error('Load earnings data error:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMore = async () => {
    if (!hasMore) return;
    try {
      const nextPage = page + 1;
      const txnRes = await driverService.getTransactions(nextPage, 20);
      if (txnRes?.success) {
        setTransactions(prev => [...prev, ...txnRes.data.transactions]);
        setHasMore(txnRes.data.pagination.page < txnRes.data.pagination.pages);
        setPage(nextPage);
      }
    } catch (error) {
      console.error('Load more transactions error:', error);
    }
  };

  const handleCashOut = async () => {
    if (!wallet || wallet.balance <= 0) {
      if (isWeb) window.alert('No balance available to cash out');
      return;
    }

    const confirmed = isWeb
      ? window.confirm(`Cash out $${wallet.balance.toFixed(2)} to your payment method?`)
      : true; // On native, use Alert.alert with callback

    if (!confirmed) return;

    setCashingOut(true);
    try {
      const response = await driverService.cashOut();
      if (response?.success) {
        if (isWeb) window.alert(response.message || `Successfully cashed out $${response.data.amountCashedOut.toFixed(2)}`);
        loadData(); // Refresh all data
      } else {
        if (isWeb) window.alert(response?.message || 'Cash out failed');
      }
    } catch (error) {
      const msg = error.response?.data?.message || 'Cash out failed';
      if (isWeb) window.alert(msg);
    } finally {
      setCashingOut(false);
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatTime = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  };

  const renderTransaction = ({ item }) => {
    const isEarning = item.type === 'RIDE_EARNING';
    const isCashOut = item.type === 'CASH_OUT';

    return (
      <View style={styles.txnCard}>
        <View style={styles.txnIconContainer}>
          <View style={[styles.txnIcon, { backgroundColor: isEarning ? 'rgba(76, 175, 80, 0.12)' : isCashOut ? 'rgba(33, 150, 243, 0.12)' : 'rgba(255, 152, 0, 0.12)' }]}>
            <Ionicons
              name={isEarning ? 'car' : isCashOut ? 'wallet' : 'swap-horizontal'}
              size={20}
              color={isEarning ? '#4CAF50' : isCashOut ? '#2196F3' : '#FF9800'}
            />
          </View>
        </View>
        <View style={styles.txnInfo}>
          <Text style={styles.txnTitle}>
            {isEarning ? 'Ride Earning' : isCashOut ? 'Cash Out' : item.type.replace('_', ' ')}
          </Text>
          <Text style={styles.txnDate}>{formatDate(item.createdAt)} at {formatTime(item.createdAt)}</Text>
          {item.ride && (
            <Text style={styles.txnRoute} numberOfLines={1}>
              {item.ride.origin} → {item.ride.destination}
            </Text>
          )}
        </View>
        <View style={styles.txnAmountContainer}>
          <Text style={[styles.txnAmount, { color: item.amount >= 0 ? '#4CAF50' : '#F44336' }]}>
            {item.amount >= 0 ? '+' : ''}{item.amount.toFixed(2)}
          </Text>
          <Text style={styles.txnBalance}>Bal: ${item.balance.toFixed(2)}</Text>
        </View>
      </View>
    );
  };

  const ListHeader = () => (
    <View>
      {/* Balance Card */}
      <View style={styles.balanceCard}>
        <LinearGradient
          colors={['#1a1a2e', '#16213e', '#0f3460']}
          style={styles.balanceGradient}
        >
          <Text style={styles.balanceLabel}>AVAILABLE BALANCE</Text>
          <Text style={styles.balanceAmount}>
            ${wallet ? wallet.balance.toFixed(2) : '0.00'}
          </Text>

          <TouchableOpacity
            style={[styles.cashOutButton, (!wallet || wallet.balance <= 0 || cashingOut) && styles.cashOutDisabled]}
            onPress={handleCashOut}
            disabled={!wallet || wallet.balance <= 0 || cashingOut}
          >
            <LinearGradient
              colors={wallet && wallet.balance > 0 ? ['#D4AF37', '#B8860B'] : ['#555', '#444']}
              style={styles.cashOutGradient}
            >
              {cashingOut ? (
                <ActivityIndicator color="#000" size="small" />
              ) : (
                <>
                  <Ionicons name="wallet-outline" size={20} color="#000" />
                  <Text style={styles.cashOutText}>CASH OUT</Text>
                </>
              )}
            </LinearGradient>
          </TouchableOpacity>
        </LinearGradient>
      </View>

      {/* Earnings Summary */}
      <View style={styles.summaryRow}>
        <View style={styles.summaryCard}>
          <LinearGradient colors={['#1e1e1e', '#2d2d2d']} style={styles.summaryGradient}>
            <Ionicons name="today" size={24} color="#D4AF37" />
            <Text style={styles.summaryAmount}>
              ${wallet ? wallet.todayEarnings.toFixed(2) : '0.00'}
            </Text>
            <Text style={styles.summaryLabel}>Today</Text>
            <Text style={styles.summarySubLabel}>
              {wallet ? wallet.todayRides : 0} rides
            </Text>
          </LinearGradient>
        </View>

        <View style={styles.summaryCard}>
          <LinearGradient colors={['#1e1e1e', '#2d2d2d']} style={styles.summaryGradient}>
            <Ionicons name="calendar" size={24} color="#2196F3" />
            <Text style={styles.summaryAmount}>
              ${wallet ? wallet.weekEarnings.toFixed(2) : '0.00'}
            </Text>
            <Text style={styles.summaryLabel}>This Week</Text>
            <Text style={styles.summarySubLabel}>
              {wallet ? wallet.weekRides : 0} rides
            </Text>
          </LinearGradient>
        </View>

        <View style={styles.summaryCard}>
          <LinearGradient colors={['#1e1e1e', '#2d2d2d']} style={styles.summaryGradient}>
            <Ionicons name="trending-up" size={24} color="#4CAF50" />
            <Text style={styles.summaryAmount}>
              ${wallet ? wallet.lifetimeEarnings.toFixed(2) : '0.00'}
            </Text>
            <Text style={styles.summaryLabel}>Lifetime</Text>
            <Text style={styles.summarySubLabel}>
              {wallet ? wallet.lifetimeRides : 0} rides
            </Text>
          </LinearGradient>
        </View>
      </View>

      {/* Transaction History Header */}
      <View style={styles.sectionHeader}>
        <Text style={styles.sectionTitle}>Transaction History</Text>
      </View>
    </View>
  );

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#D4AF37" />
          <Text style={styles.loadingText}>Loading earnings...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <FlatList
        data={transactions}
        renderItem={renderTransaction}
        keyExtractor={(item) => item.id}
        ListHeaderComponent={ListHeader}
        ListEmptyComponent={
          <View style={styles.emptyState}>
            <Ionicons name="receipt-outline" size={48} color="#555" />
            <Text style={styles.emptyTitle}>No transactions yet</Text>
            <Text style={styles.emptyText}>Complete rides to see your earnings here</Text>
          </View>
        }
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={() => { setRefreshing(true); loadData(); }}
            tintColor="#D4AF37"
          />
        }
        onEndReached={loadMore}
        onEndReachedThreshold={0.3}
        contentContainerStyle={styles.listContent}
        showsVerticalScrollIndicator={false}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0a0a0a',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },
  listContent: {
    paddingBottom: 30,
  },
  balanceCard: {
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 20,
    overflow: 'hidden',
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
  },
  balanceGradient: {
    paddingVertical: 28,
    paddingHorizontal: 24,
    alignItems: 'center',
  },
  balanceLabel: {
    fontSize: 13,
    color: 'rgba(255,255,255,0.5)',
    letterSpacing: 2,
    fontWeight: '600',
    marginBottom: 8,
  },
  balanceAmount: {
    fontSize: 48,
    fontWeight: '900',
    color: '#ffffff',
    marginBottom: 20,
  },
  cashOutButton: {
    borderRadius: 14,
    overflow: 'hidden',
    width: '100%',
  },
  cashOutDisabled: {
    opacity: 0.5,
  },
  cashOutGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    gap: 10,
  },
  cashOutText: {
    fontSize: 16,
    fontWeight: '800',
    color: '#000',
    letterSpacing: 1.5,
  },
  summaryRow: {
    flexDirection: 'row',
    paddingHorizontal: 12,
    marginTop: 16,
    gap: 8,
  },
  summaryCard: {
    flex: 1,
    borderRadius: 14,
    overflow: 'hidden',
  },
  summaryGradient: {
    padding: 14,
    alignItems: 'center',
    minHeight: 120,
    justifyContent: 'center',
  },
  summaryAmount: {
    fontSize: 20,
    fontWeight: '800',
    color: '#ffffff',
    marginTop: 8,
    marginBottom: 2,
  },
  summaryLabel: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    fontWeight: '500',
  },
  summarySubLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.35)',
    marginTop: 2,
  },
  sectionHeader: {
    paddingHorizontal: 20,
    paddingTop: 24,
    paddingBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#ffffff',
  },
  txnCard: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#1a1a1a',
    marginHorizontal: 16,
    marginBottom: 8,
    borderRadius: 12,
    padding: 14,
  },
  txnIconContainer: {
    marginRight: 12,
  },
  txnIcon: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: 'center',
    alignItems: 'center',
  },
  txnInfo: {
    flex: 1,
  },
  txnTitle: {
    fontSize: 15,
    fontWeight: '600',
    color: '#ffffff',
    marginBottom: 2,
  },
  txnDate: {
    fontSize: 12,
    color: 'rgba(255,255,255,0.4)',
  },
  txnRoute: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    marginTop: 2,
  },
  txnAmountContainer: {
    alignItems: 'flex-end',
    marginLeft: 8,
  },
  txnAmount: {
    fontSize: 17,
    fontWeight: '800',
  },
  txnBalance: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.3)',
    marginTop: 2,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 40,
    paddingHorizontal: 30,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#888',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#555',
    textAlign: 'center',
    lineHeight: 20,
  },
});

export default EarningsScreen;
